// How much of an Inbound row (= one devanning group) has actually gone out, worked out from the
// Release/Delivery Notes raised against it (Gary, 2026-09-29): a row can have several notes
// (split shipments), each carrying its own NOP, and the row is "dispatched" once the notes add up.
//
// Counting rules -- shared by the Inbound list and the Notes page so they can't disagree:
//  - only ACTIVE notes (VOID and SUPERSEDED release their quantity back);
//  - only notes that were actually generated: a bare autosaved DRAFT with no PDF isn't a
//    dispatch yet, anything Planned/Completed or with a PDF is;
//  - the primary unit is cartons (delivery_notes.nop).
export interface DispatchNote {
  id: string
  serial_no: number
  status: 'ACTIVE' | 'SUPERSEDED' | 'VOID'
  lifecycle_status: 'DRAFT' | 'PLANNED' | 'COMPLETED'
  pdf_url: string | null
  transit_status?: 'PENDING' | 'IN_TRANSIT' | 'DELIVERED' | null
  nop: number
  item_ids: string[] | null
  source_devanning_item_id?: string | null
}

export type DispatchState = 'none' | 'partial' | 'done' | 'over'

export interface DispatchProgress {
  dispatched: number
  total: number
  state: DispatchState
  breakdown: { serial: number; nop: number }[]
  // Display-only stage split (Gary, 2026-09-29): "Dispatched" = goods really left = COMPLETED.
  // `dispatched` above stays "covered by a generated note" -- the over-quantity guard on the Notes
  // page relies on that -- so don't show it as a dispatch figure.
  completed: number   // COMPLETED notes
  planned: number     // PLANNED notes (in Planned Outbound, still in the warehouse)
  drafted: number     // DRAFT notes, including autosaved ones with no PDF
  noted: number       // number of active notes of any stage
}

// Goods have physically left the warehouse: a finished note, or a Collection+Delivery note that
// was confirmed collected and is now in transit (its lifecycle stays PLANNED until POD, but the
// goods are gone -- leaving the warehouse and arriving are two separate moments).
export function hasLeftWarehouse(n: DispatchNote): boolean {
  return n.lifecycle_status === 'COMPLETED' || n.transit_status === 'IN_TRANSIT' || n.transit_status === 'DELIVERED'
}

export function countsAsDispatched(n: DispatchNote): boolean {
  return n.status === 'ACTIVE' && (n.lifecycle_status !== 'DRAFT' || !!n.pdf_url)
}

// `itemIds` = the devanning_item ids that make up the row/group. New notes carry an explicit
// source_devanning_item_id; older ones only have item_ids, so fall back to overlap with those.
export function noteMatchesSource(n: DispatchNote, itemIds: string[]): boolean {
  if (n.source_devanning_item_id) return itemIds.includes(n.source_devanning_item_id)
  return (n.item_ids || []).some(id => itemIds.includes(id))
}

export function dispatchProgress(total: number, notes: DispatchNote[], itemIds: string[]): DispatchProgress {
  const counted = notes.filter(n => countsAsDispatched(n) && noteMatchesSource(n, itemIds))
  const dispatched = counted.reduce((s, n) => s + (n.nop || 0), 0)
  const active = notes.filter(n => n.status === 'ACTIVE' && noteMatchesSource(n, itemIds))
  const sum = (pick: (n: DispatchNote) => boolean) => active.filter(pick).reduce((a, n) => a + (n.nop || 0), 0)
  const breakdown = counted.map(n => ({ serial: n.serial_no, nop: n.nop || 0 }))
  let state: DispatchState
  if (counted.length === 0) state = 'none'
  else if (total <= 0 || dispatched === total) state = 'done'
  else state = dispatched < total ? 'partial' : 'over'
  return { dispatched, total, state, breakdown, completed: sum(hasLeftWarehouse), planned: sum(n => n.lifecycle_status === 'PLANNED' && !hasLeftWarehouse(n)), drafted: sum(n => n.lifecycle_status === 'DRAFT'), noted: active.length }
}

// Weight / CBM / pallets of a partial shipment: the row's totals scaled by the share of cartons
// this note takes (full quantity -> untouched). Staff can still override any of them.
export function scaleForQuantity(value: number | null, nop: number, total: number, round: 'ceil' | 'none' = 'none', dp = 2): number | null {
  if (value == null) return null
  if (total <= 0 || nop >= total) return value
  const v = value * Math.max(nop, 0) / total
  if (round === 'ceil') return Math.max(Math.ceil(v), nop > 0 ? 1 : 0)
  const f = 10 ** dp
  return Math.round(v * f) / f
}
